import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { textFilter, selectFilter, numberFilter, dateFilter } from 'react-bootstrap-table2-filter';
import { HeaderCell, SortCaret, RowActionCell } from '../components';
import { currencyFormatter } from 'utils';
function getFilter(filterType) {
    switch (filterType) {
        case 'none':
            return undefined;
        case 'singleSelect':
            return selectFilter({
                options: {}
            });
        case 'number':
            return numberFilter({});
        case 'date':
            return dateFilter({});
        case 'text':
        default:
            return textFilter();
    }
}
function configureColumn(col, withFilter, withSort, withExport, isFirstChild, sumKeys) {
    return Object.assign(Object.assign({}, col), { isDummyField: false, csvExport: withExport, filter: !withFilter ? undefined : col.filter ? col.filter : getFilter(col.filterType), sort: withSort, headerFormatter: HeaderCell, sortCaret: SortCaret, footer: isFirstChild && sumKeys ? 'Total' : calculateFooter(col, sumKeys), footerStyle: isFirstChild
            ? { fontWeight: 'bold', fontFamily: 'Paragraph' }
            : { textAlign: 'right' } });
}
function calculateFooter(col, sumKeys) {
    return sumKeys === undefined
        ? undefined
        : (sumKeys === null || sumKeys === void 0 ? void 0 : sumKeys.includes(col.dataField))
            ? (columnData) => currencyFormatter(columnData.reduce((acc, item) => acc + parseFloat(item || 0), 0))
            : '';
}
function createRowActionsColumn(rowActions, hasFooter) {
    return {
        dataField: 'actions',
        text: '',
        isDummyField: true,
        csvExport: true,
        filter: undefined,
        sort: false,
        headerFormatter: HeaderCell,
        headerStyle: {
            width: '80px',
            textAlign: 'right'
        },
        sortCaret: (order) => _jsx(_Fragment, {}),
        formatter: (cell, row) => !row.id.toString().includes('header') ? (_jsx(RowActionCell, Object.assign({ row: row }, rowActions))) : (''),
        footer: hasFooter ? '' : undefined,
        footerStyle: { textAlign: 'left' }
    };
}
/**
 * `useTableColumns` is a custom hook for generating column configurations for the `Table` component.
 * It utilizes `react-bootstrap-table-next` for defining the structure and behavior of table columns,
 * including options for sorting, filtering, and custom render components.
 *
 * @template TData - The type of data to be displayed in the table. It should be a record (object) type.
 * @param {Array<ColumnDescription<TData, keyof TData & string>>} columns - An array of column descriptions.
 * @param {boolean} withFilter - Flag to enable/disable filtering for all columns.
 * @param {boolean} withSort - Flag to enable/disable sorting for all columns.
 * @param {boolean} withExport - Flag to enable/disable export functionality for all columns.
 * @param {Array<string>} [sumKeys] - Optional array of keys for columns that should be summed.
 * @returns {Array<ColumnDescription<TData, keyof TData & string>>} - An array of configured column descriptions.
 */
export function useTableColumns(originalColumns, withFilter, withSort, withExport, sumKeys, rowActions) {
    return useMemo(() => {
        let cols = originalColumns.map((col, i) => configureColumn(col, withFilter, withSort, withExport, i === 0, sumKeys));
        if (rowActions) {
            cols.push(createRowActionsColumn(rowActions, sumKeys !== undefined));
        }
        return cols;
    }, [originalColumns, withFilter, withSort, withExport, sumKeys, rowActions]);
}
